/**
 * Privacy Governance API Client
 * PROJ-082 - Privacy Governance Canvas Integration
 */

import { fetchWithAuth } from './http-client';
import { parseStandardApiResponse } from './standard-api';
import type {
  NodePrivacyGovernance,
  DpiaAssessment,
  ComplianceScoreBreakdown,
  ApplicableLawsResponse,
  FlowComplianceSummary,
  RopaFormat,
  ReportData,
} from '@/types/privacy-governance';

const API_BASE = (import.meta.env.VITE_API_URL || '/api/v1').replace(/\/$/, '');
const API_ENV_VAR = 'VITE_API_URL';

// ============================================================================
// Node-level Privacy Governance
// ============================================================================

export async function getNodeGovernance(nodeId: string): Promise<NodePrivacyGovernance> {
  const response = await fetchWithAuth(`${API_BASE}/privacy-governance/node/${nodeId}`, {
    context: 'Gobernanza de privacidad del nodo',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  });

  const { data } = await parseStandardApiResponse<NodePrivacyGovernance>(
    response,
    'Gobernanza de privacidad del nodo'
  );

  return data;
}

export async function updateNodeGovernance(
  nodeId: string,
  payload: Partial<NodePrivacyGovernance>
): Promise<NodePrivacyGovernance> {
  const response = await fetchWithAuth(`${API_BASE}/privacy-governance/node/${nodeId}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
    context: 'Actualizar gobernanza de privacidad',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  });

  const { data } = await parseStandardApiResponse<NodePrivacyGovernance>(
    response,
    'Actualizar gobernanza de privacidad'
  );

  return data;
}

export async function assessDpia(nodeId: string): Promise<DpiaAssessment> {
  const response = await fetchWithAuth(`${API_BASE}/privacy-governance/node/${nodeId}/dpia`, {
    context: 'Evaluacion DPIA del nodo',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  });

  const { data } = await parseStandardApiResponse<DpiaAssessment>(
    response,
    'Evaluacion DPIA del nodo'
  );

  return data;
}

export async function getComplianceScore(nodeId: string): Promise<ComplianceScoreBreakdown> {
  const response = await fetchWithAuth(`${API_BASE}/privacy-governance/node/${nodeId}/score`, {
    context: 'Puntaje de cumplimiento del nodo',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  });

  const { data } = await parseStandardApiResponse<ComplianceScoreBreakdown>(
    response,
    'Puntaje de cumplimiento del nodo'
  );

  return data;
}

export async function getApplicableLaws(nodeId: string): Promise<ApplicableLawsResponse> {
  const response = await fetchWithAuth(
    `${API_BASE}/privacy-governance/node/${nodeId}/applicable-laws`,
    {
      context: 'Leyes aplicables al nodo',
      envVar: API_ENV_VAR,
      baseUrl: API_BASE,
    }
  );

  const { data } = await parseStandardApiResponse<ApplicableLawsResponse>(
    response,
    'Leyes aplicables al nodo'
  );

  return data;
}

// ============================================================================
// Flow-level Compliance Summary
// ============================================================================

export async function getFlowSummary(flowId: string): Promise<FlowComplianceSummary> {
  const response = await fetchWithAuth(`${API_BASE}/privacy-governance/flow/${flowId}/summary`, {
    context: 'Resumen de cumplimiento del flujo',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  });

  const { data } = await parseStandardApiResponse<FlowComplianceSummary>(
    response,
    'Resumen de cumplimiento del flujo'
  );

  return data;
}

// ============================================================================
// Flow-level Report Generation
// ============================================================================

export async function generateReport(flowId: string, format: RopaFormat = 'generic'): Promise<ReportData> {
  const response = await fetchWithAuth(
    `${API_BASE}/privacy-governance/flow/${flowId}/report?format=${encodeURIComponent(format)}`,
    {
      context: 'Generar reporte de cumplimiento',
      envVar: API_ENV_VAR,
      baseUrl: API_BASE,
    }
  );

  // For CSV format, the backend returns raw CSV text (not JSON)
  if (format === 'csv') {
    const csvContent = await response.text();
    return {
      format: 'csv',
      generatedAt: new Date().toISOString(),
      flowName: '',
      flowId,
      totalNodes: 0,
      columns: [],
      rows: [],
      csvContent,
    };
  }

  const { data } = await parseStandardApiResponse<ReportData>(
    response,
    'Generar reporte de cumplimiento'
  );

  return data;
}

export async function getFlowScore(flowId: string): Promise<ComplianceScoreBreakdown> {
  const response = await fetchWithAuth(`${API_BASE}/privacy-governance/flow/${flowId}/score`, {
    context: 'Puntaje de cumplimiento del flujo',
    envVar: API_ENV_VAR,
    baseUrl: API_BASE,
  });

  const { data } = await parseStandardApiResponse<ComplianceScoreBreakdown>(
    response,
    'Puntaje de cumplimiento del flujo'
  );

  return data;
}
